import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Card,
  Dialog,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Pagination,
  CircularProgress,
  TextField,
  Box,
  Button,
  Tabs,
  Tab,
  Chip,
} from "@mui/material";
import { motion } from "framer-motion";
import EmailIcon from "@mui/icons-material/Email";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { blue, green, orange, purple, pink } from "@mui/material/colors";
import * as XLSX from "xlsx";
import { toast } from 'react-toastify';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const EnquiryDashboard = () => {
  const [dashboardData, setDashboardData] = useState({});
  const [enquiriesData, setEnquiriesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [currentTab, setCurrentTab] = useState(0);
  const rowsPerPage = 5;
  const [expandedEnquiryId, setExpandedEnquiryId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedEnquiryId, setSelectedEnquiryId] = useState(null);
  const [startDate, setStartDate] = React.useState(dayjs().subtract(1, 'month'));
  const [endDate, setEndDate] = React.useState(dayjs());

  const handleOpenDeleteDialog = (enquiryId) => {
    setSelectedEnquiryId(enquiryId);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedEnquiryId(null);
  };

  useEffect(() => {
    getDashboardData();
    getEnquiriesData();
  }, []);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [page]);

  const handleChangePage = (event, value) => {
    setPage(value);
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setPage(1); // Reset to first page when changing tabs
  };

  const handleDownloadExcel = () => {
    const dataToExport = [];
    const exportData = getFilteredEnquiries().sort((a, b) => a.id - b.id);
    let serialNo = 1;

    exportData.forEach((enquiry) => {
      dataToExport.push({
        "S.NO": serialNo++,
        "Enquiry ID": enquiry.id,
        "Customer Name": enquiry.name || "",
        Email: enquiry.email || "",
        Mobile: enquiry.phone || "",
        "Enquiry Status": enquiry.status,
        "Enquiry Date": new Date(enquiry.created_at).toLocaleString(),
        "Source": getEnquirySource(enquiry),
        Message: enquiry.message || "",
        "Product ID": enquiry.product_id || "N/A",
      });

      // Add a blank row between enquiries
      dataToExport.push({});
    });

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Enquiries");

    XLSX.writeFile(workbook, "Enquiries_Report.xlsx");
  };

  const getEnquirySource = (enquiry) => {
    if (enquiry.is_enquiry_from_banner) return "Banner Page";
    if (enquiry.is_enquiry_from_contact_us_page) return "Contact Us Page";
    if (enquiry.is_enquiry_from_brochure_download) return "Brochure Download";
    return "Product Page";
  };

  const getFilteredEnquiries = () => {
    return enquiriesData.filter((enquiry) => {
      const enquiryDate = new Date(enquiry.created_at);
      const matchesSearch = enquiry.id
        .toString()
        .toLowerCase()
        .includes(search.toLowerCase()) ||
        enquiry.name?.toLowerCase().includes(search.toLowerCase()) ||
        enquiry.email?.toLowerCase().includes(search.toLowerCase()) ||
        enquiry.phone?.toLowerCase().includes(search.toLowerCase());

      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;

      if (end) {
        end.setHours(23, 59, 59, 999);
      }

      const isInDateRange =
        (!start || start <= enquiryDate) && (!end || end >= enquiryDate);

      // Filter by tab
      let matchesTab = true;
      switch (currentTab) {
        case 1: // Banner
          matchesTab = enquiry.is_enquiry_from_banner;
          break;
        case 2: // Contact Us
          matchesTab = enquiry.is_enquiry_from_contact_us_page;
          break;
        case 3: // Brochure
          matchesTab = enquiry.is_enquiry_from_brochure_download;
          break;
        case 4: // Product
          matchesTab = !enquiry.is_enquiry_from_banner &&
            !enquiry.is_enquiry_from_contact_us_page &&
            !enquiry.is_enquiry_from_brochure_download;
          break;
        default: // All
          matchesTab = true;
      }

      return matchesSearch && isInDateRange && matchesTab;
    });
  };

  const filteredEnquiries = getFilteredEnquiries();
  const paginatedEnquiries = filteredEnquiries.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const stats = [
    {
      title: "Total",
      value: dashboardData.totalEnquiries || 0,
      icon: <EmailIcon fontSize="large" />,
      color: blue[500],
    },
    {
      title: "Banner",
      value: dashboardData.bannerEnquiries || 0,
      icon: <EmailIcon fontSize="large" />,
      color: pink[500],
    },
    {
      title: "Contact-Us",
      value: dashboardData.contactUsEnquiries || 0,
      icon: <EmailIcon fontSize="large" />,
      color: purple[500],
    },
    {
      title: "Brochure",
      value: dashboardData.brochureEnquiries || 0,
      icon: <EmailIcon fontSize="large" />,
      color: green[500],
    },
    {
      title: "Product",
      value: dashboardData.productEnquiries || 0,
      icon: <EmailIcon fontSize="large" />,
      color: orange[500],
    },
    {
      title: "Completed",
      value: dashboardData.completedEnquiries || 0,
      icon: <CheckCircleIcon fontSize="large" />,
      color: green[500],
    },
  ];

  const getDashboardData = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE}/enquiries/dashboard`);
      setDashboardData(response.data);
    } catch (err) {
      toast.error("Failed to load dashboard data");
    }
  };

  const getEnquiriesData = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE}/enquiries/`);
      setEnquiriesData(response.data.sort((a, b) => b.id - a.id));
    } catch (err) {
      toast.error("Failed to load enquiries data");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEnquiry = async () => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_BASE}/enquiries/${selectedEnquiryId}`
      );
      setEnquiriesData((prev) =>
        prev.filter((enquiry) => enquiry.id !== selectedEnquiryId)
      );
      handleCloseDeleteDialog();
      getDashboardData(); // Refresh counts
      toast.success("Enquiry deleted successfully");
    } catch (error) {
      toast.error("Failed to delete enquiry");
    }
  };

  const handleUpdateStatus = async (enquiryId, newStatus) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE}/enquiries/${enquiryId}`,
        { status: newStatus }
      );
      setEnquiriesData(prev => prev.map(enquiry =>
        enquiry.id === enquiryId ? { ...enquiry, status: newStatus } : enquiry
      ));
      getDashboardData(); // Refresh counts
      toast.success("Status updated successfully");
    } catch (error) {
      toast.error("Failed to update enquiry status");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return green[500];
      case 'In Progress': return orange[500];
      default: return blue[500];
    }
  };

  const getSourceColor = (enquiry) => {
    if (enquiry.is_enquiry_from_banner) return pink[500];
    if (enquiry.is_enquiry_from_contact_us_page) return purple[500];
    if (enquiry.is_enquiry_from_brochure_download) return green[500];
    return orange[500];
  };

  return (
    <Box sx={{ padding: 4, backgroundColor: "#f4f5fa", minHeight: "100vh" }}>
      <Grid container spacing={2}> {/* Reduced spacing between cards */}
        {stats.map((stat, index) => (
          <Grid item xs={6} sm={4} md={3} lg={2} key={index}>
            <motion.div whileHover={{ scale: 1.03 }} transition={{ duration: 0.2 }}>
              <Card sx={{
                height: '100%',
                borderRadius: '10px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}>
                <CardContent sx={{ p: 1.5 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="caption" sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                        lineHeight: 1.2
                      }}>
                        {stat.title}
                      </Typography>
                      <Typography variant="h6" sx={{
                        fontWeight: 700,
                        fontSize: '1.1rem',
                        lineHeight: 1.2,
                        mt: 0.5
                      }}>
                        {stat.value}
                      </Typography>
                    </Box>
                    <Avatar sx={{
                      width: 36,
                      height: 36,
                      bgcolor: stat.color,
                      '& svg': {
                        fontSize: '1rem'
                      }
                    }}>
                      {stat.icon}
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      <Box mt={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" mb={2}>
              Enquiry Management
            </Typography>

            <Tabs value={currentTab} onChange={handleTabChange} sx={{ mb: 3 }}>
              <Tab label="All" />
              <Tab label="Banner Section" />
              <Tab label="Contact-Us Section" />
              <Tab label="Brochure Section" />
              <Tab label="Product Section" />
            </Tabs>

            <Box
              display="flex"
              gap={2}
              flexWrap="wrap"
              alignItems="center"
              mb={3}
              sx={{
                backgroundColor: "#f9f9f9",
                padding: 2,
                borderRadius: 2,
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    slotProps={{
                      textField: {
                        sx: {
                          minWidth: 200,
                          backgroundColor: "#fff",
                          borderRadius: 1,
                        },
                      },
                    }}
                  />
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    slotProps={{
                      textField: {
                        sx: {
                          minWidth: 200,
                          backgroundColor: "#fff",
                          borderRadius: 1,
                        },
                      },
                    }}
                  />
                </Box>
              </LocalizationProvider>
              <Button
                variant="contained"
                color="success"
                onClick={handleDownloadExcel}
                sx={{
                  height: "fit-content",
                  mt: { xs: 1, sm: 0 },
                  fontWeight: "bold",
                  textTransform: "none",
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                }}
              >
                ⬇️ Download Report
              </Button>
            </Box>

            <Box mt={2} display="flex" justifyContent="center">
              <Pagination
                count={Math.ceil(filteredEnquiries.length / rowsPerPage)}
                page={page}
                onChange={handleChangePage}
                color="primary"
                shape="rounded"
                style={{ paddingBottom: '10px' }}
              />
            </Box>

            <TextField
              fullWidth
              label="🔍 Search by Enquiry ID, Name, Email or Phone"
              variant="outlined"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ mb: 2 }}
            />

            {loading ? (
              <Box display="flex" justifyContent="center" my={4}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Enquiry ID</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedEnquiries.map((enquiry, index) => (
                      <React.Fragment key={index}>
                        <TableRow>
                          <TableCell>{enquiry.id}</TableCell>
                          <TableCell>
                            <Typography fontWeight="bold">{enquiry.name}</Typography>
                            {enquiry.product_id && (
                              <Typography variant="caption">Product: {enquiry.product_id}</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography>{enquiry.email}</Typography>
                            <Typography>{enquiry.phone}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getEnquirySource(enquiry)}
                              sx={{
                                backgroundColor: getSourceColor(enquiry),
                                color: 'white',
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={enquiry.status}
                              sx={{
                                backgroundColor: getStatusColor(enquiry.status),
                                color: 'white',
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {new Date(enquiry.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={() => handleOpenDeleteDialog(enquiry.id)}
                              style={{ marginRight: "8px", marginBottom: "8px" }}
                            >
                              Delete
                            </Button>
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              onClick={() =>
                                setExpandedEnquiryId(
                                  expandedEnquiryId === enquiry.id
                                    ? null
                                    : enquiry.id
                                )
                              }
                              style={{ marginRight: "8px", marginBottom: "8px" }}
                            >
                              {expandedEnquiryId === enquiry.id
                                ? "Hide"
                                : "Details"}
                            </Button>
                          </TableCell>
                        </TableRow>
                        {expandedEnquiryId === enquiry.id && (
                          <TableRow>
                            <TableCell colSpan={7}>
                              <Box
                                sx={{
                                  background: "#F5FCFF",
                                  p: 3,
                                  borderRadius: 2,
                                  borderLeft: `4px solid ${getSourceColor(enquiry)}`,
                                }}
                              >
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle1" gutterBottom>
                                      <strong>Enquiry Details</strong>
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                      <strong>ID:</strong> {enquiry.id}
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                      <strong>Customer:</strong> {enquiry.name}
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                      <strong>Email:</strong> {enquiry.email}
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                      <strong>Phone:</strong> {enquiry.phone}
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                      <strong>Source:</strong>{" "}
                                      <Chip
                                        label={getEnquirySource(enquiry)}
                                        sx={{
                                          backgroundColor: getSourceColor(enquiry),
                                          color: 'white',
                                          ml: 1
                                        }}
                                      />
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle1" gutterBottom>
                                      <strong>Additional Information</strong>
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                      <strong>Product:</strong> {enquiry.product_id || "N/A"}
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                      <strong>Status:</strong>{" "}
                                      <Chip
                                        label={enquiry.status}
                                        sx={{
                                          backgroundColor: getStatusColor(enquiry.status),
                                          color: 'white',
                                          ml: 1
                                        }}
                                      />
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                      <strong>Date:</strong>{" "}
                                      {new Date(enquiry.created_at).toLocaleString()}
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                      <strong>Message:</strong>
                                    </Typography>
                                    <Box sx={{
                                      p: 2,
                                      backgroundColor: '#f0f0f0',
                                      borderRadius: 1,
                                      maxHeight: 150,
                                      overflowY: 'auto'
                                    }}>
                                      <Typography variant="body2">
                                        {enquiry.message || "No message provided"}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                </Grid>

                                <Box mt={3} display="flex" gap={2} flexWrap="wrap">
                                  <Button
                                    variant="contained"
                                    color="success"
                                    onClick={() => handleUpdateStatus(enquiry.id, "Completed")}
                                    disabled={enquiry.status === "Completed"}
                                    size="small"
                                  >
                                    Mark Completed
                                  </Button>
                                  <Button
                                    variant="contained"
                                    color="warning"
                                    onClick={() => handleUpdateStatus(enquiry.id, "In Progress")}
                                    disabled={enquiry.status === "In Progress"}
                                    size="small"
                                  >
                                    Mark In Progress
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    onClick={() => setExpandedEnquiryId(null)}
                                    size="small"
                                  >
                                    Close Details
                                  </Button>
                                </Box>
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
          <Box mt={2} display="flex" justifyContent="center">
            <Pagination
              count={Math.ceil(filteredEnquiries.length / rowsPerPage)}
              page={page}
              onChange={handleChangePage}
              color="primary"
              shape="rounded"
              style={{ paddingBottom: '20px' }}
            />
          </Box>
          <Dialog
            open={openDeleteDialog}
            onClose={handleCloseDeleteDialog}
            aria-labelledby="delete-dialog-title"
          >
            <Card sx={{ p: 3 }}>
              <Typography id="delete-dialog-title" variant="h6">
                Confirm Deletion
              </Typography>
              <Typography variant="body2" sx={{ my: 2 }}>
                Are you sure you want to delete Enquiry ID: {selectedEnquiryId}?
              </Typography>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
                <Button
                  onClick={handleDeleteEnquiry}
                  variant="contained"
                  color="error"
                >
                  Delete
                </Button>
              </Box>
            </Card>
          </Dialog>
        </Card>
      </Box>
    </Box>
  );
};

export default EnquiryDashboard;

